import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import { motion } from 'framer-motion';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ShieldIcon from '@mui/icons-material/Shield';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link } from 'react-router-dom';

const CmmcServices: React.FC = () => {
  const cmmcLevels = [
    { level: "Level 1 – Foundational", description: "Basic cyber hygiene; 17 practices to safeguard Federal Contract Information (FCI)." },
    { level: "Level 2 – Advanced", description: "Advanced practices aligned with NIST SP 800-171; protects CUI across 72 practices." },
    { level: "Level 3 – Expert", description: "Expert-level practices; reduces risk from advanced persistent threats (APTs)." }
  ];

  const cmmcServices = [
    {
      title: "CMMC Consultancy Services",
      description: "Our consultancy helps you implement CMMC practices, close gaps, and prepare for certification—from gap analysis through pre-assessment readiness.",
      benefits: [
        "Gap analysis and readiness assessment",
        "Control implementation and documentation",
        "CMMC training and awareness",
        "Pre-assessment preparation"
      ],
      path: "/services/cmmc/consultancy"
    },
    {
      title: "CMMC Assessment Services",
      description: "We assess your cybersecurity posture against CMMC requirements and help you build the evidence and practices needed for a successful formal assessment.",
      benefits: [
        "Objective evaluation of CUI protection maturity",
        "Structured assessment process",
        "Clear findings and recommendations",
        "Pathway to certification"
      ],
      path: "/services/cmmc/assessment"
    }
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0c2340 0%, #1a365d 100%)',
          color: 'white',
          position: 'relative',
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom textAlign="center">
              CMMC Services
            </Typography>
            <Typography variant="h6" paragraph align="center" sx={{ maxWidth: '800px', mx: 'auto', opacity: 0.9 }}>
              Cybersecurity Maturity Model Certification solutions to protect CUI and meet DoD requirements
            </Typography>
          </motion.div>
        </Container>
        
        <Box sx={{
          position: 'absolute',
          top: 60,
          right: 120,
          width: '150px',
          height: '150px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: 40,
          left: 100,
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />
      </Box>

      {/* Introduction Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Typography variant="h4" component="h2" gutterBottom fontWeight="bold" color="#0c2340">
                What is CMMC?
              </Typography>
              <Typography paragraph>
                The Cybersecurity Maturity Model Certification (CMMC) is a DoD framework that ensures defense
                contractors and subcontractors adequately protect Controlled Unclassified Information (CUI).
                CMMC aligns with NIST SP 800-171 and is required for many DoD contracts.
              </Typography>
              <Typography paragraph>
                CMMC defines three maturity levels—Foundational, Advanced, and Expert—across 17 domains
                including Access Control, Audit and Accountability, and Configuration Management. Achieving
                certification demonstrates your commitment to protecting CUI and supports your ability to
                compete for and fulfill DoD work.
              </Typography>
            </motion.div>
          </Box>
          <Box sx={{ flex: '1 1 45%', minWidth: '300px' }}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Box
                component="img"
                src="https://images.unsplash.com/photo-1563986768609-322da13575f3?ixlib=rb-1.2.1&auto=format&fit=crop&w=1470&q=80"
                alt="CMMC Cybersecurity"
                sx={{ width: '100%', borderRadius: '16px', boxShadow: '0 10px 30px rgba(0,0,0,0.1)' }}
              />
            </motion.div>
          </Box>
        </Box>
      </Container>

      {/* CMMC Levels Section */}
      <Box sx={{ bgcolor: '#f5f7fa', py: 10 }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <Typography variant="h4" component="h2" gutterBottom fontWeight="bold" color="#0c2340" textAlign="center" sx={{ mb: 6 }}>
              CMMC Maturity Levels
            </Typography>
          </motion.div>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3 }}>
            {cmmcLevels.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    height: '100%',
                    borderRadius: '16px',
                    boxShadow: '0 5px 20px rgba(0,0,0,0.05)',
                    borderTop: '4px solid #1976d2',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-10px)',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                    }
                  }}
                >
                  <Typography variant="h6" component="h3" fontWeight="bold" color="primary" gutterBottom>
                    {item.level}
                  </Typography>
                  <Typography color="text.secondary">{item.description}</Typography>
                </Paper>
              </motion.div>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Our CMMC Services Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <Typography variant="h4" component="h2" gutterBottom fontWeight="bold" color="#0c2340" textAlign="center" sx={{ mb: 6 }}>
            Our CMMC Services
          </Typography>
        </motion.div>
        
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 4 }}>
          {cmmcServices.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 5,
                  height: '100%',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 16px 40px rgba(0,0,0,0.12)',
                  }
                }}
              >
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    backgroundColor: '#1976d215',
                    color: '#1976d2',
                    mb: 3
                  }}
                >
                  <ShieldIcon sx={{ fontSize: 40 }} />
                </Box>
                
                <Typography variant="h5" component="h3" fontWeight="bold" gutterBottom color="#0c2340">
                  {service.title}
                </Typography>
                
                <Typography paragraph color="text.secondary" sx={{ mb: 3 }}>
                  {service.description}
                </Typography>
                
                <Divider sx={{ my: 3 }} />
                
                <Typography variant="h6" gutterBottom fontWeight="medium">
                  Key Benefits:
                </Typography>
                
                <List sx={{ mb: 3 }}>
                  {service.benefits.map((benefit, idx) => (
                    <ListItem key={idx} disablePadding sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={benefit} />
                    </ListItem>
                  ))}
                </List>
                
                <Button
                  variant="outlined"
                  color="primary"
                  endIcon={<ArrowForwardIcon />}
                  component={Link}
                  to={service.path}
                  sx={{ borderRadius: '8px', '&:hover': { backgroundColor: '#1976d215' } }}
                >
                  Learn More
                </Button>
              </Paper>
            </motion.div>
          ))}
        </Box>
      </Container>

      {/* CTA Section */}
      <Box sx={{ backgroundColor: '#f5f7fa', py: 12, position: 'relative', overflow: 'hidden' }}>
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Paper
              elevation={0}
              sx={{
                p: { xs: 4, md: 6 },
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #0c2340 0%, #1a365d 100%)',
                color: 'white',
                textAlign: 'center',
                boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                position: 'relative'
              }}
            >
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Ready to Achieve CMMC Certification?
              </Typography>
              <Typography variant="body1" paragraph sx={{ maxWidth: '700px', mx: 'auto', mb: 4, opacity: 0.9 }}>
                Our CMMC experts can help you protect CUI, implement required controls, and prepare for
                certification. Contact us today for a free consultation.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  component={Link}
                  to="/contact?service=cmmc"
                  sx={{
                    bgcolor: 'white',
                    color: '#0c2340',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                    px: 4,
                    py: 1.5,
                    borderRadius: '8px',
                    fontWeight: 'bold'
                  }}
                >
                  Contact Us
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  component={Link}
                  to="/services"
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
                    px: 4,
                    py: 1.5,
                    borderRadius: '8px'
                  }}
                >
                  View All Services
                </Button>
              </Box>
              <Box sx={{ position: 'absolute', top: -30, left: -30, width: 100, height: 100, borderRadius: '50%', background: 'rgba(255,255,255,0.03)', zIndex: 0 }} />
              <Box sx={{ position: 'absolute', bottom: -40, right: -40, width: 150, height: 150, borderRadius: '50%', background: 'rgba(255,255,255,0.03)', zIndex: 0 }} />
            </Paper>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default CmmcServices;
